import React, { useMemo } from 'react'
import { Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { Bar } from 'react-chartjs-2'
import ReportSummaryCards from './ReportSummaryCards'
import { operatorReportStyles as opStyles } from '../../../helpers/style'
import { normalizeDisposition, normalizeDirection, CALL_DIRECTIONS } from '../../../helpers/demo'

const MONTH_LABEL_KEYS = ['monthJanuary', 'monthFebruary', 'monthMarch', 'monthApril', 'monthMay', 'monthJune', 'monthJuly', 'monthAugust', 'monthSeptember', 'monthOctober', 'monthNovember', 'monthDecember']

function getPeriodKey(dateStr, period) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  if (period === 'today') return String(d.getHours()).padStart(2, '0')
  if (period === 'month') return String(d.getDate())
  return `${y}-${m}-${day}`
}

function formatPeriodLabel(key, period, langIndex = 1, dateFrom, dateTo) {
  if (!key) return key
  const locale = langIndex === 2 ? 'ru-RU' : langIndex === 3 ? 'hy-AM' : 'en-US'
  if (period === 'today') return `${key}:00`
  if (period === 'week') {
    const [y, mo, d] = key.split('-')
    const date = new Date(parseInt(y, 10), parseInt(mo, 10) - 1, parseInt(d, 10))
    return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' })
  }
  if (period === 'month') {
    const ref = dateTo ? new Date(dateTo) : dateFrom ? new Date(dateFrom) : new Date()
    const monthName = ref.toLocaleDateString(locale, { month: 'short' })
    return `${key} ${monthName}`
  }
  const [y, m, day] = key.split('-')
  const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${parseInt(day, 10)} ${monthNames[parseInt(m, 10)] || m}`
}

function getFullPeriodKeys(period, dateFrom, dateTo) {
  if (period === 'today') {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
  }
  if (period === 'week' && dateFrom && dateTo) {
    const keys = []
    const from = new Date(dateFrom)
    const to = new Date(dateTo)
    for (let d = new Date(from); d <= to; d.setDate(d.getDate() + 1)) {
      keys.push(d.toISOString().slice(0, 10))
    }
    return keys
  }
  if (period === 'month' && (dateFrom || dateTo)) {
    const ref = dateTo ? new Date(dateTo) : dateFrom ? new Date(dateFrom) : new Date()
    const year = ref.getFullYear()
    const month = ref.getMonth()
    const lastDay = new Date(year, month + 1, 0).getDate()
    return Array.from({ length: lastDay }, (_, i) => String(i + 1))
  }
  return []
}

function getCustomMonthKeys(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return []
  const from = new Date(dateFrom)
  const to = new Date(dateTo)
  const keys = []
  const curr = new Date(from.getFullYear(), from.getMonth(), 1)
  const end = new Date(to.getFullYear(), to.getMonth(), 1)
  while (curr <= end) {
    keys.push(`${curr.getFullYear()}-${String(curr.getMonth() + 1).padStart(2, '0')}`)
    curr.setMonth(curr.getMonth() + 1)
  }
  return keys
}

export default function CallCountPage({
  report,
  period = 'week',
  dateFrom,
  dateTo,
  opTr,
  langIndex,
  getCallTypeLabel,
  getDirectionLabel,
  historyData,
  historyFacetFields,
  graphicStatusData,
  graphicDispositionData,
  historyGraphicLoading,
  historyGraphicError,
  ...rest
}) {
  const year = useMemo(() => {
    const d = dateTo ? new Date(dateTo) : dateFrom ? new Date(dateFrom) : new Date()
    return d.getFullYear()
  }, [dateTo, dateFrom])

  const callsForCharts = useMemo(() => {
    const docs = historyData?.response?.docs
    if (docs && Array.isArray(docs) && docs.length > 0) {
      const mapped = docs.map((doc) => {
        const main = doc.items?.find((i) => i.uniqueid === doc.linkedid) || doc.items?.[0]
        if (!main) return null
        return {
          start: main.start,
          disposition: main.disposition,
          direction: main.status || main.direction || (main.line && main.line !== 'null' ? 'outgoing' : 'incoming'),
          status: main.status,
        }
      }).filter(Boolean)
      if (mapped.length > 0) return mapped
    }
    return report?.calls ?? []
  }, [historyData?.response?.docs, report?.calls])

  const periodTableData = useMemo(() => {
    const dirKeys = CALL_DIRECTIONS.map((d) => d.key)
    const calls = callsForCharts
    const locale = langIndex === 2 ? 'ru-RU' : langIndex === 3 ? 'hy-AM' : 'en-US'

    const emptyRow = (dateKey) => {
      const row = { dateKey, count: 0 }
      dirKeys.forEach((k) => { row[k] = 0 })
      return row
    }

    const graphic = graphicStatusData || graphicDispositionData
    const buckets = graphic?.buckets
    if (buckets && Array.isArray(buckets) && buckets.length > 0) {
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const statusValToDir = (val) => {
        const v = String(val || '').toLowerCase().trim()
        if (v.includes('incoming') || v === 'in') return 'incoming'
        if (v.includes('out') && v.includes('call')) return 'outgoing'
        if (v.includes('local')) return 'local'
        if (v.includes('transfer')) return 'transferred'
        return null
      }
      const typeValToDir = (val) => {
        const v = String(val || '').toUpperCase().trim()
        if (v === 'INCOMING' || v === 'INCOMING CALL') return 'incoming'
        if (v === 'OUTGOING' || v === 'OUT CALL') return 'outgoing'
        if (v === 'LOCAL' || v === 'LOCAL CALL') return 'local'
        if (v === 'TRANSFERRED' || v === 'REDIRECT CALL') return 'transferred'
        return null
      }
      const getDirCounts = (b) => {
        const row = { count: 0 }
        dirKeys.forEach((k) => { row[k] = 0 })
        const byType = b?.by_type?.buckets
        if (Array.isArray(byType)) {
          byType.forEach((t) => {
            const dir = typeValToDir(t?.val) ?? statusValToDir(t?.val)
            if (dir && dirKeys.includes(dir)) {
              const n = Number(t.count) || 0
              row[dir] = n
              row.count += n
            }
          })
        }
        const byDisp = b?.by_disp?.buckets
        if (Array.isArray(byDisp) && !Array.isArray(byType)) {
          byDisp.forEach((d) => { row.count += Number(d.count) || 0 })
        }
        return row
      }
      const rows = buckets.map((b) => {
        let label = '—'
        if (b && b.val != null) {
          const raw = String(b.val)
          const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
          if (isoMatch) {
            const [, y, m, day, hh, mm] = isoMatch
            const month = monthNames[parseInt(m, 10)] || m
            label = `${parseInt(day, 10)} ${month}, ${hh}:${mm}`
          } else {
            const d = new Date(b.val)
            if (!Number.isNaN(d.getTime())) {
              const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
              const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
              label = `${datePart}, ${timePart}`
            } else label = raw
          }
        }
        return { dateKey: label, ...getDirCounts(b) }
      })
      return {
        type: 'graphic',
        rows,
        headerLabelKey: 'time',
        labelGetter: (row) => row.dateKey,
      }
    }

    if (period === 'today') {
      const byPeriod = {}
      const selectedDate = (dateFrom || dateTo || new Date().toISOString().slice(0, 10)).slice(0, 10)
      calls.forEach((c) => {
        if (!c.start) return
        if (c.start.slice(0, 10) !== selectedDate) return
        const key = getPeriodKey(c.start, 'today')
        if (!byPeriod[key]) byPeriod[key] = emptyRow(key)
        const dir = normalizeDirection(c)
        if (byPeriod[key][dir] !== undefined) byPeriod[key][dir] += 1
        byPeriod[key].count += 1
      })
      const fullKeys = getFullPeriodKeys('today', dateFrom, dateTo)
      fullKeys.forEach((k) => { if (!byPeriod[k]) byPeriod[k] = emptyRow(k) })
      const rows = fullKeys.map((k) => byPeriod[k])
      return {
        type: 'today',
        rows,
        headerLabelKey: 'time',
        labelGetter: (row) => formatPeriodLabel(row.dateKey, 'today', langIndex, dateFrom, dateTo),
      }
    }

    if (period === 'week') {
      const byPeriod = {}
      calls.forEach((c) => {
        if (!c.start) return
        const key = getPeriodKey(c.start, 'week')
        if (!byPeriod[key]) byPeriod[key] = emptyRow(key)
        const dir = normalizeDirection(c)
        if (byPeriod[key][dir] !== undefined) byPeriod[key][dir] += 1
        byPeriod[key].count += 1
      })
      const fullKeys = getFullPeriodKeys('week', dateFrom, dateTo)
      fullKeys.forEach((k) => { if (!byPeriod[k]) byPeriod[k] = emptyRow(k) })
      const rows = fullKeys.map((k) => byPeriod[k])
      return {
        type: 'day',
        rows,
        headerLabelKey: 'date',
        labelGetter: (row) => formatPeriodLabel(row.dateKey, 'week', langIndex, dateFrom, dateTo),
      }
    }

    if (period === 'month') {
      const ref = dateTo ? new Date(dateTo) : dateFrom ? new Date(dateFrom) : new Date()
      const year = ref.getFullYear()
      const month = ref.getMonth()
      const byPeriod = {}
      calls.forEach((c) => {
        if (!c.start) return
        const d = new Date(c.start)
        if (d.getFullYear() !== year || d.getMonth() !== month) return
        const key = getPeriodKey(c.start, 'month')
        if (!byPeriod[key]) byPeriod[key] = emptyRow(key)
        const dir = normalizeDirection(c)
        if (byPeriod[key][dir] !== undefined) byPeriod[key][dir] += 1
        byPeriod[key].count += 1
      })
      const fullKeys = getFullPeriodKeys('month', dateFrom, dateTo)
      fullKeys.forEach((k) => { if (!byPeriod[k]) byPeriod[k] = emptyRow(k) })
      const rows = fullKeys.map((k) => byPeriod[k])
      return {
        type: 'month',
        rows,
        headerLabelKey: 'date',
        labelGetter: (row) => formatPeriodLabel(row.dateKey, 'month', langIndex, dateFrom, dateTo),
      }
    }

    if (period === 'custom') {
      const fromDate = dateFrom ? new Date(dateFrom) : null
      const toDate = dateTo ? new Date(dateTo) : null
      if (!fromDate || !toDate) return { type: 'day', rows: [], headerLabelKey: 'date', labelGetter: () => '' }

      const daysDiff = Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1

      if (daysDiff <= 31) {
        const byPeriod = {}
        calls.forEach((c) => {
          if (!c.start) return
          const key = c.start.slice(0, 10)
          if (!byPeriod[key]) byPeriod[key] = emptyRow(key)
          const dir = normalizeDirection(c)
          if (byPeriod[key][dir] !== undefined) byPeriod[key][dir] += 1
          byPeriod[key].count += 1
        })
        const fullKeys = getFullPeriodKeys('week', dateFrom, dateTo)
        fullKeys.forEach((k) => { if (!byPeriod[k]) byPeriod[k] = emptyRow(k) })
        const rows = fullKeys.map((k) => byPeriod[k])
        return {
          type: 'day',
          rows,
          headerLabelKey: 'date',
          labelGetter: (row) => new Date(row.dateKey).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }),
        }
      }

      const byPeriod = {}
      calls.forEach((c) => {
        if (!c.start) return
        const d = new Date(c.start)
        const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        if (!byPeriod[monthKey]) {
          byPeriod[monthKey] = { dateKey: monthKey, monthKey, count: 0 }
          dirKeys.forEach((k) => { byPeriod[monthKey][k] = 0 })
        }
        const dir = normalizeDirection(c)
        if (byPeriod[monthKey][dir] !== undefined) byPeriod[monthKey][dir] += 1
        byPeriod[monthKey].count += 1
      })
      const fullKeys = getCustomMonthKeys(dateFrom, dateTo)
      fullKeys.forEach((k) => {
        if (!byPeriod[k]) {
          byPeriod[k] = { dateKey: k, monthKey: k, count: 0 }
          dirKeys.forEach((dk) => { byPeriod[k][dk] = 0 })
        }
      })
      const rows = fullKeys.map((k) => byPeriod[k])
      return {
        type: 'month',
        rows,
        headerLabelKey: 'month',
        labelGetter: (row) => {
          const [y, m] = (row.monthKey || row.dateKey || '').split('-')
          const monthName = opTr[MONTH_LABEL_KEYS[parseInt(m, 10) - 1]]?.[langIndex] ?? new Date(0, parseInt(m, 10) - 1).toLocaleDateString(locale, { month: 'short' })
          return `${monthName} ${y}`
        },
      }
    }

    return { type: 'day', rows: [], headerLabelKey: 'date', labelGetter: () => '' }
  }, [graphicStatusData, graphicDispositionData, callsForCharts, period, dateFrom, dateTo, langIndex, opTr])

  const groupedChartData = useMemo(() => {
    const graphic = graphicStatusData || graphicDispositionData
    const buckets = graphic?.buckets
    if (buckets && Array.isArray(buckets) && buckets.length > 0) {
      const monthNames = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const labels = buckets.map((b) => {
        if (!b || b.val == null) return '—'
        const raw = String(b.val)
        const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
        if (isoMatch) {
          const [, y, m, day, hh, mm] = isoMatch
          const month = monthNames[parseInt(m, 10)] || m
          return `${parseInt(day, 10)} ${month}, ${hh}:${mm}`
        }
        const d = new Date(b.val)
        if (!Number.isNaN(d.getTime())) {
          const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
          const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', hour12: false })
          return `${datePart}, ${timePart}`
        }
        return raw
      })
      const dispToKey = (val) => {
        const v = String(val || '').toUpperCase().trim()
        if (v === 'ANSWERED') return 'answered'
        if (v === 'ANSWER LATER') return 'answeredLater'
        return 'notAnswered'
      }
      const getDispCounts = (b) => {
        const out = { answered: 0, answeredLater: 0, notAnswered: 0 }
        let dispBuckets = b?.by_disp?.buckets
        if (!Array.isArray(dispBuckets)) {
          const byType = b?.by_type?.buckets
          if (Array.isArray(byType)) dispBuckets = byType.flatMap((t) => t?.by_disp?.buckets || [])
        }
        if (Array.isArray(dispBuckets)) {
          dispBuckets.forEach((d) => {
            const key = dispToKey(d?.val)
            const n = Number(d.count) || 0
            out[key] += n
          })
        }
        return out
      }
      const byBucket = buckets.map(getDispCounts)
      const answeredLabel = getCallTypeLabel('answered')
      const answeredLaterLabel = getCallTypeLabel('answered_later')
      const notAnsweredLabel = getCallTypeLabel('not_answered')
      return {
        labels,
        datasets: [
          {
            label: answeredLabel,
            data: byBucket.map((v) => v.answered),
            backgroundColor: '#22c55e',
            borderColor: '#16a34a',
            borderWidth: 1,
            stack: 'success',
          },
          {
            label: answeredLaterLabel,
            data: byBucket.map((v) => v.answeredLater),
            backgroundColor: '#6ee7b7',
            borderColor: '#34d399',
            borderWidth: 1,
            stack: 'success',
          },
          {
            label: notAnsweredLabel,
            data: byBucket.map((v) => v.notAnswered),
            backgroundColor: '#ef4444',
            borderColor: '#dc2626',
            borderWidth: 1,
            stack: 'failed',
          },
        ],
      }
    }
    const byPeriod = {}
    callsForCharts.forEach((c) => {
      const key = getPeriodKey(c.start, period)
      if (!key) return
      if (!byPeriod[key]) byPeriod[key] = { answered: 0, answeredLater: 0, notAnswered: 0 }
      const type = normalizeDisposition(c.disposition)
      if (type === 'answered') byPeriod[key].answered += 1
      else if (type === 'answered_later') byPeriod[key].answeredLater += 1
      else byPeriod[key].notAnswered += 1
    })
    const fullKeys = getFullPeriodKeys(period, dateFrom, dateTo)
    const keysToUse = fullKeys.length > 0 ? fullKeys : Object.keys(byPeriod).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
    keysToUse.forEach((k) => {
      if (!byPeriod[k]) byPeriod[k] = { answered: 0, answeredLater: 0, notAnswered: 0 }
    })
    const sorted = keysToUse.map((k) => [k, byPeriod[k]])
    const labels = sorted.map(([k]) => formatPeriodLabel(k, period, langIndex, dateFrom, dateTo))
    const answeredLabel = getCallTypeLabel('answered')
    const answeredLaterLabel = getCallTypeLabel('answered_later')
    const notAnsweredLabel = getCallTypeLabel('not_answered')
    return {
      labels,
      datasets: [
        {
          label: answeredLabel,
          data: sorted.map(([, v]) => v.answered),
          backgroundColor: '#22c55e',
          borderColor: '#16a34a',
          borderWidth: 1,
          stack: 'success',
        },
        {
          label: answeredLaterLabel,
          data: sorted.map(([, v]) => v.answeredLater),
          backgroundColor: '#6ee7b7',
          borderColor: '#34d399',
          borderWidth: 1,
          stack: 'success',
        },
        {
          label: notAnsweredLabel,
          data: sorted.map(([, v]) => v.notAnswered),
          backgroundColor: '#ef4444',
          borderColor: '#dc2626',
          borderWidth: 1,
          stack: 'failed',
        },
      ],
    }
  }, [graphicStatusData, graphicDispositionData, callsForCharts, report?.calls, period, dateFrom, dateTo, langIndex, getCallTypeLabel, opTr])

  const getDirLabel = (key) => (getDirectionLabel ? getDirectionLabel(key) : (opTr[CALL_DIRECTIONS.find((d) => d.key === key)?.labelKey]?.[langIndex] ?? key))

  return (
    <>
      <ReportSummaryCards
        report={report}
        opTr={opTr}
        langIndex={langIndex}
        getCallTypeLabel={getCallTypeLabel}
        formatDuration={rest.formatDuration}
        historyData={historyData}
        historyFacetFields={historyFacetFields}
        graphicStatusData={graphicStatusData}
        graphicDispositionData={graphicDispositionData}
        historyGraphicLoading={historyGraphicLoading}
        historyGraphicError={historyGraphicError}
        {...rest}
      />
      <Box sx={opStyles.sectionBox}>
        <Paper sx={opStyles.chartPaperWide}>
          <Typography variant="subtitle1" sx={opStyles.chartTitle}>
            {opTr.callsPerDay?.[langIndex]}
          </Typography>
          <Box sx={opStyles.chartBox}>
            <Bar
              data={groupedChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'top', display: true },
                },
                scales: {
                  x: {
                    type: 'category',
                    grid: { display: false },
                    stacked: true,
                  },
                  y: {
                    beginAtZero: true,
                    ticks: { stepSize: 1 },
                    stacked: true,
                  },
                },
              }}
            />
          </Box>
        </Paper>
      </Box>

      <Box sx={opStyles.sectionBox}>
        <Paper sx={opStyles.chartPaperWide}>
          <Typography variant="subtitle1" sx={opStyles.chartTitle}>
            {period === 'today' ? (opTr.callsByHour?.[langIndex] ?? 'Calls by hour') : period === 'week' || (period === 'custom' && periodTableData.type === 'day') ? (opTr.callsByDay?.[langIndex] ?? 'Calls by day') : (opTr.callsByMonth?.[langIndex] ?? 'Calls by month')}
          </Typography>
          <TableContainer sx={{ ...opStyles.tableContainer, maxHeight: 'none', overflow: 'visible' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {periodTableData.headerLabelKey === 'time' ? (opTr.time?.[langIndex] ?? 'Time') : periodTableData.headerLabelKey === 'month' ? (opTr.month?.[langIndex] ?? 'Month') : (opTr.date?.[langIndex] ?? 'Date')}
                  </TableCell>
                  {CALL_DIRECTIONS.map((d) => (
                    <TableCell key={d.key} align="center" sx={{ fontWeight: 600 }}>{getDirLabel(d.key)}</TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{opTr.total?.[langIndex]}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {periodTableData.rows.map((row, idx) => (
                  <TableRow key={row.dateKey || row.monthKey || idx}>
                    <TableCell align="center">{periodTableData.labelGetter(row)}</TableCell>
                    {CALL_DIRECTIONS.map((d) => (
                      <TableCell key={d.key} align="center">{row[d.key] ?? 0}</TableCell>
                    ))}
                    <TableCell align="center">{row.count}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ backgroundColor: 'action.hover' }}>
                  <TableCell align="center" sx={{ fontWeight: 600 }}>{opTr.total?.[langIndex]}</TableCell>
                  {CALL_DIRECTIONS.map((d) => (
                    <TableCell key={d.key} align="center" sx={{ fontWeight: 600 }}>
                      {periodTableData.rows.reduce((sum, row) => sum + (row[d.key] ?? 0), 0)}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 600 }}>
                    {periodTableData.rows.reduce((sum, row) => sum + row.count, 0)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  )
}
